import React, { useState, useEffect, useMemo } from 'react';
import Select, { SingleValue } from 'react-select';

interface OptionType {
  value: string; // Country: Iso2 Code, City: City Name
  label: string; // Country: Name, City: City Name
}

interface LocationValue {
  country: OptionType | null;
  city: OptionType | null;
}

interface LocationSelectorProps {
  value: LocationValue;
  onChange: (value: LocationValue) => void;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({ value, onChange }) => {
  const [countries, setCountries] = useState<OptionType[]>([]);
  const [cities, setCities] = useState<OptionType[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      setError(null);
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/iso');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data.error) {
          throw new Error(data.msg || 'Failed to fetch countries');
        }
        const formattedCountries = data.data
          .map((country: { name: string; Iso2: string }) => ({
            value: country.Iso2,
            label: country.name,
          }))
          .sort((a: OptionType, b: OptionType) => {
            // Keep Turkey pinned at top as the primary market.
            if (a.value === 'TR' && b.value !== 'TR') return -1;
            if (b.value === 'TR' && a.value !== 'TR') return 1;
            return a.label.localeCompare(b.label);
          });
        setCountries(formattedCountries);
      } catch (e: any) {
        
        setError(`Failed to load countries: ${e.message}`);
        setCountries([]); // Clear countries on error
      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []); // Empty dependency array means run once on mount

  // Fetch cities when country changes
  useEffect(() => {
    // Clear cities if country is deselected or not set
    if (!value.country?.label) { // Use label (country name) for the API call
      setCities([]);
      setIsLoadingCities(false); // Stop loading if country is cleared
      setError(null); // Clear previous errors
      return;
    }

    const fetchCities = async () => {
      setIsLoadingCities(true);
      setCities([]); // Clear previous cities
      setError(null);
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/cities', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ country: value.country!.label }), // API expects country name
        });

        if (!response.ok) {
          // Handle specific case where country might not have cities in the API
          if (response.status === 404 || response.status === 400) { 
             
             setCities([]); // Ensure cities are empty
             // Optionally set a specific message instead of a generic error
             setError(`No cities found for ${value.country!.label} or invalid request.`); 
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        } else {
            const data = await response.json();
            if (data.error) {
              // Handle error messages from the API
              
              setError(data.msg || `Failed to fetch cities for ${value.country!.label}.`);
              setCities([]);
            } else if (data.data && Array.isArray(data.data)) {
                const formattedCities = data.data.map((city: string) => ({
                value: city,
                label: city,
                }));
                setCities(formattedCities);
            } else {
                
                setCities([]); // Set empty if data format is wrong
            }
        }

      } catch (e: any) {
        
        setError(`Failed to load cities: ${e.message}`);
        setCities([]); // Clear cities on error
      } finally {
        setIsLoadingCities(false);
      }
    };

    fetchCities();
  }, [value.country]); // Re-run effect when value.country changes

  const handleCountryChange = (selectedOption: SingleValue<OptionType>) => {
    // When country changes, reset city
    onChange({ country: selectedOption, city: null });
  };

  const handleCityChange = (selectedOption: SingleValue<OptionType>) => {
    onChange({ ...value, city: selectedOption });
  };
  
  // Custom styles for react-select to match shadcn input
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '40px',
      borderColor: 'hsl(var(--input))',
      backgroundColor: 'hsl(var(--background))',
      boxShadow: 'none',
      '&:hover': {
        borderColor: 'hsl(var(--input))',
      },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: 'hsl(var(--popover))',
      color: 'hsl(var(--popover-foreground))',
      zIndex: 50, // Ensure dropdown is above other elements
    }),
    option: (base: any, { isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }) => ({
      ...base,
      backgroundColor: isSelected
        ? 'hsl(var(--primary))'
        : isFocused
        ? 'hsl(var(--accent))'
        : undefined,
      color: isSelected
        ? 'hsl(var(--primary-foreground))'
        : isFocused
        ? 'hsl(var(--accent-foreground))'
        : 'hsl(var(--popover-foreground))',
      cursor: 'pointer',
    }),
    singleValue: (base: any) => ({
      ...base,
      color: 'hsl(var(--foreground))',
      paddingLeft: '8px',
    }),
    input: (base: any) => ({
      ...base,
      color: 'hsl(var(--foreground))',
    }),
    placeholder: (base: any) => ({
        ...base,
        color: 'hsl(var(--muted-foreground))',
        fontSize: '0.875rem', // text-sm
        lineHeight: '1.25rem',
    }),
  };


  return (
    <div className="space-y-4">
      {/* Country Selector */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Ülke</label>
        <Select<OptionType>
          styles={selectStyles}
          placeholder="Ülke Seçiniz..."
          options={countries}
          value={value.country}
          onChange={handleCountryChange}
          isLoading={isLoadingCountries}
          isClearable
          isSearchable
          noOptionsMessage={() => isLoadingCountries ? 'Ülkeler yükleniyor...' : 'Ülke bulunamadı'}
        />
      </div>

      {/* City Selector */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Şehir</label>
        <Select<OptionType>
          styles={selectStyles}
          placeholder="Şehir Seçiniz..."
          options={cities}
          value={value.city}
          onChange={handleCityChange}
          isLoading={isLoadingCities}
          isDisabled={!value.country || isLoadingCities || cities.length === 0}
          isClearable
          isSearchable
          noOptionsMessage={() => {
            if (!value.country) return 'Önce bir ülke seçin';
            if (isLoadingCities) return 'Şehirler yükleniyor...';
            if (error && error.includes('No cities found')) return `Bu ülke (${value.country.label}) için şehir bulunamadı`;
            if (error) return 'Şehirler yüklenemedi';
            return 'Şehir bulunamadı';
          }}
        />
      </div>
      {error && !error.includes('No cities found') && (
          <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};

export default LocationSelector; 